import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { Clock } from 'lucide-react';
import VideoControls from '../components/VideoControls';
import DraggableCamera from '../components/DraggableCamera';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { useBehaviorAnalysis } from '../hooks/useBehaviorAnalysis';
import { type SalaryNegotiationConfig } from '../services/salaryAIService';
import type { InterviewConfig, InterviewReport, BehaviorSummary } from '../types';
import type { SalaryMessage } from '../types/salaryNegotiation';

// 挂断音效URL
const END_SOUND_URL = 'https://client-bucket.mmbang.site/audios/end.mp3';

// 预加载挂断音效
const endSoundAudio = new Audio(END_SOUND_URL);
endSoundAudio.preload = 'auto';

// 将 Blob 转换为 Base64
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      resolve(base64.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// 格式化时长
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// HR配置
const HR_CONFIG = {
  avatar: 'https://admin-bucket.mmbang.site/pics/HR.png',
  name: 'HR总监',
  voice: 'Maia',
};

// 生成HR开场白
function generateHROpening(overallScore: number): string {
  if (overallScore >= 90) {
    return `您好！首先恭喜您通过面试，技术团队对您的表现评价非常高。我是负责薪酬沟通的HR总监，想跟您聊聊薪资方面的事情。方便告诉我您目前的薪资情况吗？比如月薪总包大概是多少？`;
  } else if (overallScore >= 75) {
    return `您好，恭喜您通过面试！我是HR总监，想跟您沟通一下薪酬方面的事。首先想了解一下，您目前的薪资水平大概是怎样的？`;
  } else {
    return `您好，面试流程已经结束了。我是HR总监，想跟您沟通一下薪资。首先请问您目前的月薪是多少？`;
  }
}

export default function SalaryNegotiationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { message: antdMessage } = App.useApp();

  // 从准备页传递的数据
  const { interviewConfig, report, initialDialogue } =
    (location.state as {
      interviewConfig: InterviewConfig;
      report: InterviewReport;
      initialDialogue?: SalaryMessage[];
    }) || {};

  // 语音交互状态
  const [isMuted, setIsMuted] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHRSpeaking, setIsHRSpeaking] = useState(false);
  const [showSpeechEnded, setShowSpeechEnded] = useState(false);
  const [isEnding, setIsEnding] = useState(false); // 谈薪结束状态

  // 谈薪状态
  const [dialogueHistory, setDialogueHistory] = useState<SalaryMessage[]>([]);
  const [callDuration, setCallDuration] = useState(0); // 面试时长（秒）

  // Hooks
  const { startRecording, stopRecording, isMaxDurationReached } =
    useAudioRecorder();
  const { isSpeaking, stopSpeaking, speak, speakSalary } = useSpeechSynthesis();

  // 摄像头
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCameraOff, setIsCameraOff] = useState(true);

  // 行为分析
  const {
    currentAnalysis,
    summary: behaviorSummary,
    isModelLoaded,
    getTimelineSnapshot,
    stopAnalysis,
  } = useBehaviorAnalysis(videoRef, !isCameraOff);
  const behaviorSummaryRef = useRef<BehaviorSummary | null>(null);
  useEffect(() => {
    behaviorSummaryRef.current = behaviorSummary;
  }, [behaviorSummary]);

  const hasInitialized = useRef(false);
  const handleToggleMuteRef = useRef<() => Promise<void>>();
  const timerRef = useRef<NodeJS.Timeout>();
  const shouldEndNegotiationRef = useRef(false); // 标记是否需要结束谈薪
  const finalOfferRef = useRef<number | undefined>(); // 最终offer金额
  const latestDialogueRef = useRef<SalaryMessage[]>([]); // 最新的对话历史，用于跳转时包含最后一轮对话

  // 构建谈薪配置
  const salaryConfig: SalaryNegotiationConfig = {
    interviewConfig: interviewConfig!,
    overallScore: report?.totalScore,
    dimensions: report?.dimensions, // 传递6个维度评分给HR参考
  };

  // 结束谈薪
  const handleEndNegotiation = useCallback(
    async (finalOffer?: number, playEndSound: boolean = true) => {
      if (isEnding) return; // 防止重复点击

      // 设置结束状态，禁用按钮
      setIsEnding(true);

      // 清除计时器
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // 停止语音播放
      stopSpeaking();

      // 停止行为分析（不关摄像头）
      stopAnalysis();

      // 只有用户手动点击结束按钮时才播放结束音频
      if (playEndSound) {
        try {
          endSoundAudio.currentTime = 0;
          endSoundAudio.play().catch(() => {
            // 音频播放失败不影响主流程
          });
        } catch {
          // 音频播放失败不影响主流程
        }
      }

      // 延迟1.5秒后跳转到报告生成页
      // 使用 latestDialogueRef 确保包含最后一轮对话
      setTimeout(() => {
        navigate('/loading', {
          replace: true,
          state: {
            mode: 'generate-salary-report',
            salaryConfig,
            dialogueHistory:
              latestDialogueRef.current.length > 0
                ? latestDialogueRef.current
                : dialogueHistory,
            finalOffer,
            callDuration,
            behaviorSummary:
              getTimelineSnapshot() || behaviorSummaryRef.current,
          },
        });
      }, 1500);
    },
    [salaryConfig, navigate, callDuration, isEnding, stopAnalysis, getTimelineSnapshot],
  );

  // 切换麦克风 - 使用整合接口
  const handleToggleMute = useCallback(async () => {
    if (isRecording) {
      setIsRecording(false);
      setIsMuted(true);
      const wavBlob = await stopRecording();

      if (wavBlob) {
        setShowSpeechEnded(true);
        setTimeout(() => setShowSpeechEnded(false), 2000);

        try {
          setIsProcessing(true);
          const base64Audio = await blobToBase64(wavBlob);

          // 使用整合接口：ASR + Chat + TTS
          await speakSalary({
            dialogueHistory,
            salaryConfig,
            voice: HR_CONFIG.voice,
            audio: base64Audio,
            onStart: () => {
              // 语音开始播放时，结束思考状态
              setIsProcessing(false);
              setIsHRSpeaking(true);
            },
            onEnd: () => {
              setIsHRSpeaking(false);
              // 语音播放完毕后，如果需要结束谈薪则跳转
              if (shouldEndNegotiationRef.current) {
                shouldEndNegotiationRef.current = false;
                handleEndNegotiation(finalOfferRef.current, false);
              }
            },
            onComplete: (result) => {
              // 更新对话历史
              const userMessage: SalaryMessage = {
                role: 'user',
                content: result.userText,
                timestamp: Date.now(),
              };
              const hrMessage: SalaryMessage = {
                role: 'hr',
                content: result.hrText,
                timestamp: Date.now(),
              };
              const updatedHistory = [
                ...dialogueHistory,
                userMessage,
                hrMessage,
              ];
              setDialogueHistory(updatedHistory);

              // 保存最新对话到 ref，确保跳转时包含最后一轮对话
              latestDialogueRef.current = updatedHistory;

              // 如果 isFinal 为 true，标记为即将结束
              if (result.isFinal) {
                setIsEnding(true);
                shouldEndNegotiationRef.current = true;
                finalOfferRef.current = result.offerAmount;
              }
            },
          });
        } catch {
          antdMessage.error('语音处理失败');
          setIsProcessing(false);
        }
      }
    } else {
      if (isSpeaking || isHRSpeaking) {
        stopSpeaking();
        setIsHRSpeaking(false);
      }

      setIsMuted(false);
      setIsRecording(true);
      await startRecording();
    }
  }, [
    isRecording,
    stopRecording,
    dialogueHistory,
    salaryConfig,
    speakSalary,
    isSpeaking,
    isHRSpeaking,
    stopSpeaking,
    startRecording,
    handleEndNegotiation,
    antdMessage,
  ]);

  // 存储引用供useEffect使用
  handleToggleMuteRef.current = handleToggleMute;

  // 处理录制达到最大时长
  const handleMaxDurationReached = useCallback(() => {
    antdMessage.warning('发言最长60秒，已自动结束录制');
    if (handleToggleMuteRef.current) {
      handleToggleMuteRef.current();
    }
  }, [antdMessage]);

  // 强制结束
  const handleForceEnd = useCallback(() => {
    stopSpeaking();
    setIsHRSpeaking(false);
    handleEndNegotiation();
  }, [stopSpeaking, handleEndNegotiation]);

  // 初始化：播放开场白
  useEffect(() => {
    if (hasInitialized.current) return;

    if (!interviewConfig || !report) {
      navigate('/', { replace: true });
      return;
    }

    hasInitialized.current = true;

    // 使用从准备页传递过来的对话记录，如果没有则本地生成
    const dialogue = initialDialogue || [
      {
        role: 'hr',
        content: generateHROpening(report.totalScore),
        timestamp: Date.now(),
      },
    ];

    // 播放开场白
    const openingScript = dialogue[0]?.content || '';
    speak(
      openingScript,
      HR_CONFIG.voice,
      1,
      () => setIsHRSpeaking(true),
      () => setIsHRSpeaking(false),
    );

    setDialogueHistory(dialogue);

    // 启动计时器
    timerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    return () => {
      stopSpeaking();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [interviewConfig, report, speak, stopSpeaking, navigate]);

  // 监听录制最大时长
  useEffect(() => {
    if (isMaxDurationReached && isRecording) {
      handleMaxDurationReached();
    }
  }, [isMaxDurationReached, isRecording, handleMaxDurationReached]);

  // 摄像头流管理
  const streamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    const video = videoRef.current;

    if (!isCameraOff) {
      navigator.mediaDevices
        .getUserMedia({
          video: { width: { ideal: 320 }, height: { ideal: 240 }, facingMode: 'user' },
          audio: false,
        })
        .then((stream) => {
          streamRef.current = stream;
          const tryAttach = () => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            } else {
              setTimeout(tryAttach, 50);
            }
          };
          tryAttach();
        })
        .catch((err) => {
          console.error('摄像头开启失败:', err);
          setIsCameraOff(true);
        });
    } else {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (video) {
        video.srcObject = null;
      }
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [isCameraOff]);

  if (!interviewConfig || !report) {
    return null;
  }

  return (
    <div className="h-screen bg-[#0F1419] flex overflow-hidden">
      <div className="flex-1 flex flex-col relative">
        {/* 顶部状态栏 - 面试时长 */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex items-center gap-2 px-4 py-2">
            <Clock size={16} className="text-amber-400" />
            <span className="text-white text-sm font-mono">
              {formatDuration(callDuration)}
            </span>
          </div>
        </div>

        {/* 状态提示区域 - 只保留录音状态 */}
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center gap-2">
          {isRecording && (
            <div className="bg-[#1E293B]/95 backdrop-blur-sm px-4 py-2.5 rounded-xl flex items-center gap-3 border border-[#334155]/50 shadow-lg shadow-black/20">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-[#E2E8F0] text-sm font-medium">
                正在发言...
              </span>
            </div>
          )}
          {showSpeechEnded && (
            <div className="bg-[#1E293B]/95 backdrop-blur-sm px-4 py-2.5 rounded-xl flex items-center gap-3 border border-[#334155]/50 shadow-lg shadow-black/20">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-[#E2E8F0] text-sm font-medium">
                发言结束
              </span>
            </div>
          )}
        </div>

        {/* HR头像区域 - 参考面试页设计 */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="relative flex flex-col items-center justify-center">
            {/* 正在说话指示器 - 放在头像上方 */}
            {isHRSpeaking && (
              <div className="mb-6 text-center">
                <div className="flex items-center justify-center gap-1">
                  <span
                    className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-bounce"
                    style={{ animationDelay: '0s' }}
                  />
                  <span
                    className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-bounce"
                    style={{ animationDelay: '0.1s' }}
                  />
                  <span
                    className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  />
                </div>
              </div>
            )}

            <div className="relative flex items-center justify-center">
              {/* 外圈光环动画 */}
              {isHRSpeaking && (
                <>
                  <div
                    className="absolute w-56 h-56 md:w-72 md:h-72 rounded-full border-2 border-amber-500/30 animate-ping"
                    style={{ animationDuration: '1.5s' }}
                  />
                  <div
                    className="absolute w-56 h-56 md:w-72 md:h-72 rounded-full border border-yellow-500/20 animate-ping"
                    style={{ animationDuration: '1.5s', animationDelay: '0.3s' }}
                  />
                </>
              )}

              {/* 静态光环 */}
              <div className="absolute w-56 h-56 md:w-72 md:h-72 rounded-full bg-gradient-to-br from-amber-500/10 to-yellow-500/10 blur-xl" />

              {/* 头像容器 */}
              <div
                className={`
                  relative w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden
                  bg-gradient-to-br from-[#1E3A5F] to-[#0F172A]
                  border-2 border-amber-500/30
                  transition-all duration-300
                  ${isHRSpeaking ? 'scale-105 border-amber-400/40' : ''}
                `}
              >
                {/* 头像图片 */}
                <img
                  src={HR_CONFIG.avatar}
                  alt={HR_CONFIG.name}
                  className="w-full h-full object-cover"
                />

                {/* 说话时的声波效果 */}
                {isHRSpeaking && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    {/* 声波圆环 */}
                    <div className="absolute w-full h-full">
                      {[...Array(3)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute inset-0 rounded-full border border-amber-400/35 animate-ripple"
                          style={{
                            animationDelay: `${i * 0.3}s`,
                            animationDuration: '1.5s',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* HR名字 - 放在头像下方 */}
            <div className="mt-6 text-center">
              <h2 className="text-lg font-medium text-white">
                {HR_CONFIG.name}
              </h2>
            </div>

            {/* CSS 动画 */}
            <style>{`
              @keyframes ripple {
                0% {
                  transform: scale(1);
                  opacity: 0.6;
                }
                100% {
                  transform: scale(1.3);
                  opacity: 0;
                }
              }

              .animate-ripple {
                animation: ripple 1.5s ease-out infinite;
              }
            `}</style>
          </div>
        </div>

        {/* 用户摄像头 - 可拖拽 */}
        <DraggableCamera
          videoRef={videoRef}
          isCameraOff={isCameraOff}
          analysis={currentAnalysis}
        />

        {/* 底部控制栏 */}
        <VideoControls
          isMuted={isMuted}
          isCameraOff={isCameraOff}
          onToggleMute={handleToggleMute}
          onToggleCamera={() => setIsCameraOff((prev) => !prev)}
          onEndInterview={handleForceEnd}
          isInterviewEnded={isEnding}
          isRecording={isRecording}
          isProcessing={isProcessing}
          endInterviewTooltip="结束谈薪"
        />
      </div>
    </div>
  );
}
